import { jsx as _jsx } from "react/jsx-runtime";
export * from "./filter-expression";
export * from "./filter-relative";
export * from "./filter-adapter";
import { isFilterExpressionStructure } from "./filter-expression";
import { QueryClient, QueryClientProvider, useMutation, useQuery, useQueryClient, } from "@tanstack/react-query";
import { useMemo, } from "react";
export const defaultQueryState = {
    page: { page: 0, pageSize: 25 },
    sort: [],
    filters: [],
    search: "",
};
export const serializeQueryState = (state) => {
    const parameters = new URLSearchParams();
    parameters.set("page", String(state.page.page));
    parameters.set("pageSize", String(state.page.pageSize));
    if (state.search)
        parameters.set("q", state.search);
    if (state.sort.length > 0)
        parameters.set("sort", JSON.stringify(state.sort));
    if (state.filters.length > 0) {
        parameters.set("filters", JSON.stringify(state.filters));
    }
    if (state.savedViewId)
        parameters.set("view", state.savedViewId);
    if (state.filterExpression)
        parameters.set("tree", JSON.stringify(state.filterExpression));
    return parameters.toString();
};
const safeJsonArray = (value) => {
    if (!value)
        return [];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    }
    catch {
        return [];
    }
};
const safeNonNegativeInteger = (value, fallback) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
};
export const parseQueryState = (search) => {
    const parameters = new URLSearchParams(search);
    const savedViewId = parameters.get("view") ?? undefined;
    return {
        page: {
            page: safeNonNegativeInteger(parameters.get("page"), 0),
            pageSize: Math.min(100, Math.max(1, safeNonNegativeInteger(parameters.get("pageSize"), 25))),
        },
        sort: safeJsonArray(parameters.get("sort")),
        filters: safeJsonArray(parameters.get("filters")),
        search: parameters.get("q") ?? "",
        ...(savedViewId ? { savedViewId } : {}),
        ...(parameters.get("tree") && parameters.get("tree").length <= 8000 ? (() => {
            try {
                const parsed = JSON.parse(parameters.get("tree"));
                return isFilterExpressionStructure(parsed) ? { filterExpression: parsed } : {};
            }
            catch {
                return {};
            }
        })() : {}),
    };
};
export const createScopedQueryKey = (context, resource, state) => [
    "inteacc",
    context.tenantId,
    context.companyId ?? "all-companies",
    context.sessionId,
    resource,
    state ?? null,
];
const createClient = () => new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            retry: 1,
            refetchOnWindowFocus: false,
        },
    },
});
export const InteaccQueryProvider = ({ children, }) => {
    const resolvedClient = useMemo(() => createClient(), []);
    return (_jsx(QueryClientProvider, { client: resolvedClient, children: children }));
};
export const useInteaccQuery = ({ context, enabled = true, query, resource, state, }) => {
    const result = useQuery({
        queryKey: createScopedQueryKey(context, resource, state),
        queryFn: ({ signal }) => query({ signal }),
        enabled,
    });
    const data = result.data;
    const isEmpty = Array.isArray(data)
        ? data.length === 0
        : Boolean(data &&
            typeof data === "object" &&
            "items" in data &&
            Array.isArray(data.items) &&
            data.items.length === 0);
    return {
        data,
        error: result.error instanceof Error
            ? result.error
            : result.error
                ? new Error(String(result.error))
                : null,
        isEmpty,
        isError: result.isError,
        isFetching: result.isFetching,
        isLoading: result.isLoading,
        refresh: async () => {
            await result.refetch();
        },
    };
};
export const useInteaccPrefetch = () => {
    const client = useQueryClient();
    return async (options) => {
        await client.prefetchQuery({
            queryKey: createScopedQueryKey(options.context, options.resource, options.state),
            queryFn: ({ signal }) => options.query({ signal }),
        });
    };
};
export const useInteaccMutation = ({ context, invalidates, mutate, }) => {
    const client = useQueryClient();
    const result = useMutation({
        mutationFn: mutate,
        onSuccess: async () => {
            await Promise.all(invalidates.map((resource) => client.invalidateQueries({
                queryKey: createScopedQueryKey(context, resource).slice(0, 5),
            })));
        },
    });
    return {
        execute: result.mutateAsync,
        error: result.error instanceof Error
            ? result.error
            : result.error
                ? new Error(String(result.error))
                : null,
        isPending: result.isPending,
    };
};
//# sourceMappingURL=index.js.map